import axios, { AxiosError } from "axios";
import type { OrganizationId, OrganizationPermission } from "./organizations";
import type { ProjectId, ProjectPermission } from "./projects";
import type { AvatarId, UserId } from "./users";

export type Impersonator = {
    id: UserId;
    userName: string;
    displayName: string | null;
};

export type AuthUser = {
    id: UserId;
    userName: string;
    email: string;
    displayName: string | null;
    avatarId: AvatarId | null;
    isAdmin: boolean;
    impersonating: boolean;
    impersonator: Impersonator | null;
    organizationPermissions: Map<OrganizationId, OrganizationPermission[]>;
    projectPermissions: Map<ProjectId, ProjectPermission[]>;
};

/** A password alone is not always enough: an account with two-factor on owes a code next. */
export type LoginResult =
    { status: "ok"; user: AuthUser } | { status: "twoFactorRequired" } | { status: "failed" };

export type ExternalProvider = {
    scheme: string;
    name: string;
    /** MDI icon name, e.g. "mdi-github". */
    icon: string;
};

export type ExternalProviderLink = ExternalProvider & {
    connected: boolean;
    connectedAs: string | null;
};

export type TwoFactorStatus = {
    enabled: boolean;
    recoveryCodesLeft: number;
};

export type TwoFactorSetup = {
    /** The secret in groups of four, for people typing it in by hand. */
    sharedKey: string;
    authenticatorUri: string;
};

export type AuthApi = {
    authenticated(): Promise<AuthUser | null>;
    login(userName: string, password: string, rememberMe: boolean): Promise<LoginResult>;
    loginTwoFactor(
        code: string,
        rememberMe: boolean,
        rememberClient: boolean
    ): Promise<AuthUser | null>;
    loginRecoveryCode(code: string, rememberMe: boolean): Promise<AuthUser | null>;
    logout(): Promise<void>;

    externalProviders(): Promise<ExternalProvider[]>;
    externalLinks(): Promise<ExternalProviderLink[]>;
    unlinkExternal(scheme: string): Promise<void>;

    twoFactorStatus(): Promise<TwoFactorStatus>;
    setupTwoFactor(): Promise<TwoFactorSetup>;
    enableTwoFactor(code: string): Promise<string[]>;
    disableTwoFactor(code: string): Promise<void>;
    regenerateRecoveryCodes(code: string): Promise<string[]>;

    confirm(userId: UserId, token: string, userName: string, password: string): Promise<AuthUser>;
    change(old: string, newPass: string): Promise<void>;
    forgot(userName: string): Promise<void>;
    reset(userId: UserId, token: string, password: string): Promise<void>;

    impersonate(userId: UserId): Promise<void>;
    exitImpersonation(): Promise<void>;
};

type AuthUserResponse = Omit<AuthUser, "projectPermissions" | "organizationPermissions"> & {
    organizationPermissions: Record<OrganizationId, OrganizationPermission[]>;
    projectPermissions: Record<ProjectId, ProjectPermission[]>;
};

function formatAuthUserResponse(resp: AuthUserResponse): AuthUser {
    return {
        ...resp,
        organizationPermissions: new Map(
            Object.entries(resp.organizationPermissions).map(([k, v]) => [k as OrganizationId, v])
        ),
        projectPermissions: new Map(
            Object.entries(resp.projectPermissions).map(([k, v]) => [k as ProjectId, v])
        ),
    };
}

async function finishTwoFactorLogin(url: string, body: object): Promise<AuthUser | null> {
    try {
        const { data } = await axios.post<AuthUserResponse>(url, body);
        return formatAuthUserResponse(data);
    } catch (ex) {
        if (ex instanceof AxiosError && ex.response?.status === 400) {
            return null;
        } else {
            throw ex;
        }
    }
}

export const authApi: AuthApi = {
    async authenticated() {
        try {
            const { data: user } = await axios.get<AuthUserResponse>("/api/v0/auth/authenticated");
            return formatAuthUserResponse(user);
        } catch (ex) {
            if (ex instanceof AxiosError && ex.response?.status === 401) {
                return null;
            } else {
                throw ex;
            }
        }
    },
    async login(userName, password, rememberMe) {
        try {
            const { data } = await axios.post<AuthUserResponse | { requiresTwoFactor: true }>(
                "/api/v0/auth/login",
                { userName, password, rememberMe }
            );

            return "requiresTwoFactor" in data
                ? { status: "twoFactorRequired" }
                : { status: "ok", user: formatAuthUserResponse(data) };
        } catch (ex) {
            if (ex instanceof AxiosError && ex.response?.status === 400) {
                return { status: "failed" };
            } else {
                throw ex;
            }
        }
    },
    async loginTwoFactor(code, rememberMe, rememberClient) {
        return await finishTwoFactorLogin("/api/v0/auth/login/2fa", {
            code,
            rememberMe,
            rememberClient,
        });
    },
    async loginRecoveryCode(code, rememberMe) {
        return await finishTwoFactorLogin("/api/v0/auth/login/recovery", { code, rememberMe });
    },
    async externalProviders() {
        const { data } = await axios.get<ExternalProvider[]>("/api/v0/auth/external");
        return data;
    },
    async externalLinks() {
        const { data } = await axios.get<ExternalProviderLink[]>("/api/v0/auth/external/links");
        return data;
    },
    async unlinkExternal(scheme) {
        await axios.delete(`/api/v0/auth/external/${scheme}`);
    },
    async twoFactorStatus() {
        const { data } = await axios.get<TwoFactorStatus>("/api/v0/auth/2fa");
        return data;
    },
    async setupTwoFactor() {
        const { data } = await axios.post<TwoFactorSetup>("/api/v0/auth/2fa/setup");
        return data;
    },
    async enableTwoFactor(code) {
        const { data } = await axios.post<{ recoveryCodes: string[] }>("/api/v0/auth/2fa/enable", {
            code,
        });
        return data.recoveryCodes;
    },
    async disableTwoFactor(code) {
        await axios.post("/api/v0/auth/2fa/disable", { code });
    },
    async regenerateRecoveryCodes(code) {
        const { data } = await axios.post<{ recoveryCodes: string[] }>(
            "/api/v0/auth/2fa/recovery-codes",
            { code }
        );
        return data.recoveryCodes;
    },
    async logout() {
        await axios.post("/api/v0/auth/logout");
    },

    async change(old, newPass) {
        await axios.post("/api/v0/auth/change", { old: old, new: newPass });
    },
    async confirm(userId, token, userName, password) {
        const { data } = await axios.post<AuthUserResponse>("/api/v0/auth/confirm", {
            userId,
            token,
            userName,
            password,
        });

        return formatAuthUserResponse(data);
    },

    async forgot(userName) {
        await axios.post("/api/v0/auth/forgot", { userName });
    },
    async reset(userId, token, password) {
        await axios.post("/api/v0/auth/reset", { userId, token, password });
    },

    async impersonate(userId) {
        await axios.post(`/api/v0/auth/impersonate/${userId}`);
    },
    async exitImpersonation() {
        await axios.post("/api/v0/auth/impersonate/exit");
    },
};
